import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { CustomPagination } from "../customComponent/CustomPagination";

export interface EpochListItem {
  id: number;
  uuid: string;
  epoch: number;
  trainHistory_id: number | null;
  validHistory_id: number | null;
  historyIndex: number; // 회차
  overkillPercent: string;
  created_at: string;
}

export interface EpochModelRow extends EpochListItem {
  overkillCount?: number;
  isSaved: boolean;
}

type SortKey = "round" | "rate" | "count";

interface EpochModelCompareTableProps {
  data: EpochModelRow[];
  onSave: (selectedIds: number[]) => void;
  onViewHeatmap?: (row: EpochModelRow) => void;
  setOpenCompare?: (open: boolean) => void;
}

const RATE_THRESHOLDS = {
  low: 5.0,
  mid: 10.0,
} as const;

function parseOverkillRate(overkillPercent: string): number {
  const parsed = parseFloat(overkillPercent);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function getVerdict(rate: number): { label: string; color: "success" | "warning" | "error" } {
  if (rate < RATE_THRESHOLDS.low) return { label: "낮음", color: "success" };
  if (rate < RATE_THRESHOLDS.mid) return { label: "보통", color: "warning" };
  return { label: "높음", color: "error" };
}

function getBarColor(rate: number): string {
  if (rate < RATE_THRESHOLDS.low) return "#639922";
  if (rate < RATE_THRESHOLDS.mid) return "#BA7517";
  return "#E24B4A";
}

export default function EpochTrainModelTable({
  data,
  onSave,
  onViewHeatmap,
  setOpenCompare,
}: EpochModelCompareTableProps) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey>("round");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const ratesById = useMemo(() => {
    const map = new Map<number, number>();
    data.forEach((row) => map.set(row.id, parseOverkillRate(row.overkillPercent)));
    return map;
  }, [data]);

  const maxRate = useMemo(() => {
    const rates = Array.from(ratesById.values());
    return Math.max(...rates, 1);
  }, [ratesById]);

  const bestId = useMemo(() => {
    if (data.length === 0) return null;
    return data.reduce((a, b) => ((ratesById.get(a.id) ?? Infinity) < (ratesById.get(b.id) ?? Infinity) ? a : b)).id;
  }, [data, ratesById]);

  const sortedRows = useMemo(() => {
    const rows = [...data];
    switch (sortKey) {
      case "rate":
        return rows.sort((a, b) => (ratesById.get(a.id) ?? 0) - (ratesById.get(b.id) ?? 0));
      case "count":
        return rows.sort((a, b) => (a.overkillCount ?? 0) - (b.overkillCount ?? 0));
      case "round":
      default:
        return rows.sort((a, b) =>
          a.historyIndex !== b.historyIndex ? a.historyIndex - b.historyIndex : a.epoch - b.epoch,
        );
    }
  }, [data, sortKey, ratesById]);

  useEffect(() => {
    setPage(1);
  }, [sortKey, data]);

  const pagedRows = useMemo(
    () => sortedRows.slice((page - 1) * rowsPerPage, page * rowsPerPage),
    [sortedRows, page, rowsPerPage],
  );
  const totalPages = Math.ceil(sortedRows.length / rowsPerPage);

  const selectableRows = sortedRows.filter((r) => !r.isSaved);
  const isAllSelected = selectableRows.length > 0 && selectableRows.every((r) => selectedIds.has(r.id));
  const isIndeterminate = selectableRows.some((r) => selectedIds.has(r.id)) && !isAllSelected;

  const toggleSelect = (row: EpochModelRow) => {
    if (row.isSaved) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(row.id)) next.delete(row.id);
      else next.add(row.id);
      return next;
    });
  };

  const handleSortChange = (event: SelectChangeEvent) => {
    setSortKey(event.target.value as SortKey);
  };

  const handleSave = () => {
    onSave(Array.from(selectedIds));
    setOpenCompare(false);
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableRows.map((r) => r.id)));
    }
  };

  return (
    // 모달이 커지면 하단 바가 모달 바닥에 붙도록 높이를 채움
    <Box sx={{ pt: 2, minHeight: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          에폭 모델 비교
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="caption" color="text.secondary">
            정렬
          </Typography>
          <Select size="small" value={sortKey} onChange={handleSortChange} sx={{ fontSize: 12, minWidth: 140 }}>
            <MenuItem value="round" sx={{ fontSize: 12 }}>
              회차 · 에폭
            </MenuItem>
            <MenuItem value="rate" sx={{ fontSize: 12 }}>
              과검률 낮은 순
            </MenuItem>
            <MenuItem value="count" sx={{ fontSize: 12 }}>
              과검 수 적은 순
            </MenuItem>
          </Select>
        </Stack>
      </Stack>

      <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, width: "100%" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ fontSize: 11, color: "text.secondary" }}>No.</TableCell>
              <TableCell sx={{ fontSize: 11, color: "text.secondary" }}>회차</TableCell>
              <TableCell sx={{ fontSize: 11, color: "text.secondary" }}>에폭</TableCell>
              <TableCell sx={{ fontSize: 11, color: "text.secondary" }}>과검률</TableCell>
              <TableCell sx={{ fontSize: 11, color: "text.secondary", minWidth: 110 }} />
              <TableCell sx={{ fontSize: 11, color: "text.secondary" }}>과검 이미지</TableCell>
              <TableCell sx={{ fontSize: 11, color: "text.secondary" }}>판정</TableCell>
              <TableCell sx={{ fontSize: 11, color: "text.secondary" }}>저장 여부</TableCell>
              {onViewHeatmap && <TableCell sx={{ fontSize: 11, color: "text.secondary" }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedRows.map((row, index) => {
              const isSelected = selectedIds.has(row.id);
              const rate = ratesById.get(row.id) ?? 0;
              const verdict = getVerdict(rate);
              const barPct = Math.min(100, (rate / maxRate) * 100);
              const hasOverkillCount = typeof row.overkillCount === "number";

              return (
                <TableRow
                  id={`model-${row.id}`}
                  key={index}
                  hover
                  onClick={() => toggleSelect(row)}
                  selected={isSelected}
                  sx={{
                    cursor: row.isSaved ? "default" : "pointer",
                    "&.Mui-selected": { bgcolor: "action.selected" },
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={isSelected || row.isSaved}
                      disabled={row.isSaved}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSelect(row)}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{row.historyIndex}회차</TableCell>

                  <TableCell sx={{ fontSize: 13 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <span>Epoch {row.epoch}</span>
                      {row.id === bestId && (
                        <Chip label="최적" size="small" color="success" sx={{ height: 18, fontSize: 10 }} />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontSize: 13, fontWeight: 500, color: getBarColor(rate) }}>{index + 1}</TableCell>
                  <TableCell sx={{ fontSize: 13, fontWeight: 500, color: getBarColor(rate) }}>
                    {row.overkillPercent}
                  </TableCell>

                  <TableCell sx={{ minWidth: 110 }}>
                    <LinearProgress
                      variant="determinate"
                      value={barPct}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: "action.hover",
                        maxWidth: 80,
                        "& .MuiLinearProgress-bar": {
                          bgcolor: getBarColor(rate),
                          borderRadius: 3,
                        },
                      }}
                    />
                  </TableCell>

                  <TableCell sx={{ fontSize: 13, color: hasOverkillCount ? "text.primary" : "text.disabled" }}>
                    {hasOverkillCount ? `${row.overkillCount}장` : "-"}
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={verdict.label}
                      size="small"
                      color={verdict.color}
                      variant="outlined"
                      sx={{ height: 20, fontSize: 11 }}
                    />
                  </TableCell>

                  <TableCell
                    sx={{
                      fontSize: 12,
                      color: row.isSaved ? "success.main" : "text.secondary",
                    }}
                  >
                    {row.isSaved ? "저장됨" : "미저장"}
                  </TableCell>

                  <TableCell sx={{ fontSize: 13, color: hasOverkillCount ? "text.primary" : "text.disabled" }}>
                    {row.created_at}
                  </TableCell>

                  {onViewHeatmap && (
                    <TableCell>
                      <Button
                        size="small"
                        variant="text"
                        sx={{ fontSize: 11, minWidth: 0 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewHeatmap(row);
                        }}
                      >
                        히트맵
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ width: "100%", pb: 2 }}>
        {totalPages > 1 && (
          <CustomPagination page={page} totalPages={totalPages} onPageChange={setPage} groupSize={5} />
        )}
      </Box>
      {/* 스크롤 컨테이너(ModelCompareModal) 하단에 고정 — mt: auto로 남는 공간 아래로 밀림 */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 2,
          mt: "auto",
          py: 1.5,
          bgcolor: "background.paper",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {selectedIds.size}개 선택됨
        </Typography>
        <Button
          id="save-insp-model"
          variant="contained"
          size="small"
          disabled={selectedIds.size === 0}
          onClick={handleSave}
          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
        >
          선택 모델 저장
        </Button>
      </Stack>
    </Box>
  );
}
